import {
  ForwardRefRenderFunction,
  forwardRef,
  useContext,
  useImperativeHandle,
  useState,
} from 'react'

import { useHealthToolsCache } from '@hhgtech/hhg-components'
import {
  LOCALE_HAS_COMMUNITY_SITE,
  QUERY_HEALTH_TOOL,
} from '@hhgtech/hhg-components/constants'
import { DatePicker, Radio, Text } from '@hhgtech/hhg-components/mantine'
import { Stack } from '@mantine/core'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'

import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import { DueDateInfoType } from 'components/pages/healthTools/due-date'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { API_DATE_FORMAT, basePath } from 'interfaces/constants'
import { HEALTH_TOOL_TYPE } from 'interfaces/constants/healthTools'
import { AppContext } from 'state/appContext'

import { FormControl, StyledLastPeriodFormContainer } from './index.styled'
import { getDueDateIVF, useDueDateStore } from './utils'

type Props = {
  onSubmit?: (info: DueDateInfoType, redirectLink?: string) => void
  isEmbed?: boolean
  controlledSubmit?: boolean
}

export type HandlerProps = {
  submitForm: any
}

const DueDateFormIVF: ForwardRefRenderFunction<HandlerProps, Props> = (
  { onSubmit, isEmbed = false, controlledSubmit },
  ref,
) => {
  const { formatMessage: f } = useIntl()
  const { locale, router } = useRouterExtend()
  const {
    state: { isLoggedIn },
  } = useContext(AppContext) || {}
  const [IVFDate, setIVFDate] = useState(new Date())
  const [dayTransfer, setDayTransfer] = useState('3')

  const { getHealthToolLink } = useHealthToolsCache(locale)
  const { setIsCalculating } = useDueDateStore()
  useImperativeHandle(ref, () => ({
    submitForm: onSubmitForm,
  }))
  const onSubmitForm = async () => {
    const currentUrl = isEmbed
      ? `${window.location.origin}${basePath}${getHealthToolLink(
          HEALTH_TOOL_TYPE.DUE_DATE,
        )}`
      : window.location.origin + window.location.pathname

    const redirectLink =
      currentUrl +
      `?${QUERY_HEALTH_TOOL.type}=2&${QUERY_HEALTH_TOOL.startDate}=${dayjs(
        IVFDate,
      ).format(API_DATE_FORMAT)}&${
        QUERY_HEALTH_TOOL.additional
      }=${dayTransfer}${
        LOCALE_HAS_COMMUNITY_SITE.includes(locale) ? '&joincommunity=1' : ''
      }`

    if (controlledSubmit) {
      setIsCalculating(true)
      const info = await getDueDateIVF(IVFDate, dayTransfer)
      onSubmit && onSubmit(info, redirectLink)

      return
    }

    if (!isLoggedIn) {
      redirectToSSOWithReturnUrl(redirectLink, 'due-date')
    }
    if (isEmbed) {
      router.push(redirectLink)
    } else {
      setIsCalculating(true)
      const info = await getDueDateIVF(IVFDate, dayTransfer)
      onSubmit && onSubmit(info, redirectLink)
    }
  }
  return (
    <StyledLastPeriodFormContainer>
      <FormControl>
        <DatePicker
          label={
            <Text as="label" size="s4">
              {f({
                id: 'dueDatePage.ivf.transferDate',
              })}
            </Text>
          }
          value={IVFDate}
          onChange={(value) => {
            setIVFDate(value || new Date())
          }}
          excludeDate={(date) =>
            dayjs(date).isBefore(dayjs().subtract(43, 'week'))
          }
          size="md"
        />
      </FormControl>
      <FormControl>
        <Radio.Group
          name="dayTransfer"
          // orientation="vertical"
          value={dayTransfer}
          onChange={(val) => setDayTransfer(val)}
          size="md"
        >
          <Stack spacing="xs">
            <Radio
              value="3"
              label={f(
                {
                  id: 'dueDatePage.ivf.transferDateRadio',
                },
                {
                  number: 3,
                },
              )}
            />
            <Radio
              value="5"
              label={f(
                {
                  id: 'dueDatePage.ivf.transferDateRadio',
                },
                {
                  number: 5,
                },
              )}
            />
          </Stack>
        </Radio.Group>
      </FormControl>
    </StyledLastPeriodFormContainer>
  )
}

export default forwardRef(DueDateFormIVF)
